import { ArrowUp, Minus, X } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { AssistantText } from "./AssistantText";
import styles from "./AssistantDock.module.css";

type Message = {
  role: "assistant" | "user";
  text: string;
  sources?: string[];
  mode?: "model" | "guidance";
  streaming?: boolean;
  status?: string;
};

function contextFromPath(pathname: string) {
  if (pathname.startsWith("/knowledge")) return "knowledge" as const;
  if (pathname.startsWith("/cases")) return "case" as const;
  if (pathname.startsWith("/projects")) return "project" as const;
  return "general" as const;
}

export function AssistantDock() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState("");
  const [contextId, setContextId] = useState<string | undefined>();
  const [projectSnapshot, setProjectSnapshot] = useState<Record<string, unknown> | undefined>();
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      text: "你好，我是川小智。我会结合你正在看的页面解释概念、案例或项目步骤。",
    },
  ]);
  const endRef = useRef<HTMLDivElement>(null);
  const activeRequestRef = useRef<AbortController | null>(null);

  useEffect(() => () => activeRequestRef.current?.abort(), []);

  useEffect(() => {
    const openAssistant = (event: Event) => {
      const customEvent = event as CustomEvent<{ prompt?: string; submit?: boolean }>;
      setOpen(true);
      setMinimized(false);
      if (customEvent.detail?.prompt) {
        setInput(customEvent.detail.prompt);
        if (customEvent.detail.submit && user) setPendingPrompt(customEvent.detail.prompt);
      }
    };
    window.addEventListener("assistant:open", openAssistant);
    return () => window.removeEventListener("assistant:open", openAssistant);
  }, [user]);

  useEffect(() => {
    const updateContext = (event: Event) => {
      const detail = (event as CustomEvent<{ contextId?: string; snapshot?: Record<string, unknown> }>).detail;
      setContextId(detail?.contextId);
      setProjectSnapshot(detail?.snapshot);
    };
    window.addEventListener("assistant:context", updateContext);
    return () => window.removeEventListener("assistant:context", updateContext);
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, sending]);

  const submit = useCallback(async (override?: string) => {
    const message = (override ?? input).trim();
    if (!user) { setOpen(true); return; }
    if (!message || sending) return;
    setMessages((current) => [
      ...current,
      { role: "user", text: message },
      { role: "assistant", text: "", streaming: true, status: "正在准备回答…" },
    ]);
    setInput("");
    setSending(true);
    const controller = new AbortController();
    activeRequestRef.current = controller;
    const updateStreamingMessage = (update: (current: Message) => Message) => {
      setMessages((current) => {
        const next = [...current];
        let index = next.length - 1;
        while (index >= 0 && !(next[index].role === "assistant" && next[index].streaming)) index -= 1;
        if (index >= 0) next[index] = update(next[index]);
        return next;
      });
    };
    try {
      await api.assistantStream(
        {
          message,
          context_type: contextFromPath(location.pathname),
          context_id: contextId,
          project_snapshot: contextFromPath(location.pathname) === "project" ? projectSnapshot : undefined,
        },
        (event) => {
          if (event.type === "status") {
            updateStreamingMessage((current) => ({ ...current, status: event.message }));
          } else if (event.type === "meta") {
            updateStreamingMessage((current) => ({ ...current, sources: event.sources, mode: event.mode }));
          } else if (event.type === "delta") {
            updateStreamingMessage((current) => ({ ...current, text: current.text + event.delta, status: undefined }));
          } else if (event.type === "done") {
            updateStreamingMessage((current) => ({
              ...current,
              sources: event.sources,
              mode: event.mode,
              streaming: false,
              status: undefined,
            }));
          }
        },
        controller.signal,
      );
    } catch (error) {
      if (controller.signal.aborted) return;
      updateStreamingMessage((current) => ({
        ...current,
        text: current.text || (error instanceof Error ? error.message : "暂时无法连接助手，请稍后再试。"),
        streaming: false,
        status: undefined,
      }));
    } finally {
      if (activeRequestRef.current === controller) activeRequestRef.current = null;
      setSending(false);
    }
  }, [contextId, input, location.pathname, projectSnapshot, sending, user]);

  useEffect(() => {
    if (!pendingPrompt || sending) return;
    const prompt = pendingPrompt;
    setPendingPrompt("");
    void submit(prompt);
  }, [pendingPrompt, sending, submit]);

  // The project workspace has its own context-aware coach in the right rail.
  // Hiding the global launcher there avoids two competing AI entry points.
  if (location.pathname.startsWith("/projects")) return null;

  if (!user) {
    if (!open) return <button className={styles.launcher} type="button" onClick={() => setOpen(true)}><img className={styles.launcherIcon} src="/logo.png" alt="" /><span>问川小智</span></button>;
    return <aside className={styles.panel} aria-label="川小智登录提示"><header className={styles.header}><span className={styles.title}><img className={styles.assistantLogo} src="/logo.png" alt="" />川小智</span><span className={styles.controls}><button type="button" aria-label="关闭" onClick={()=>setOpen(false)}><X size={18}/></button></span></header><div className={styles.messages}><article className={styles.assistant}><AssistantText text="请登录后使用川小智。游客可以继续浏览知识图谱和案例库，登录后可获得课程问答、案例讲解和项目辅导。"/><button className="primary-button" type="button" onClick={()=>navigate("/login/student")}>学生登录</button><button className="secondary-button" type="button" onClick={()=>navigate("/login/teacher")}>教师登录</button></article></div></aside>;
  }

  if (!open) {
    return (
      <button className={styles.launcher} type="button" onClick={() => setOpen(true)}>
        <img className={styles.launcherIcon} src="/logo.png" alt="" />
        <span>问川小智</span>
      </button>
    );
  }

  return (
    <aside className={minimized ? styles.panelMinimized : styles.panel} aria-label="川小智助手">
      <header className={styles.header}>
        <span className={styles.title}>
          <img className={styles.assistantLogo} src="/logo.png" alt="" />
          川小智
        </span>
        <span className={styles.controls}>
          <button type="button" aria-label="最小化助手" onClick={() => setMinimized((value) => !value)}>
            <Minus size={18} />
          </button>
          <button type="button" aria-label="关闭助手" onClick={() => setOpen(false)}>
            <X size={18} />
          </button>
        </span>
      </header>

      {!minimized && (
        <>
          <div className={styles.messages} aria-live="polite">
            {messages.map((message, index) => (
              <article key={`${message.role}-${index}`} className={styles[message.role]}>
                {message.role === "assistant" && message.mode && (
                  <span className={styles.responseMode} data-mode={message.mode}>
                    {message.mode === "model" ? "MiniMax 已回答" : "MiniMax 暂未连接"}
                  </span>
                )}
                <AssistantText text={message.text || message.status || ""} />
                {message.sources && message.sources.length > 0 && (
                  <small>参考：{message.sources.join("、")}</small>
                )}
              </article>
            ))}
            <div ref={endRef} />
          </div>
          <div className={styles.composer}>
            <label htmlFor="assistant-input">继续提问</label>
            <div>
              <textarea
                id="assistant-input"
                value={input}
                placeholder="例如：这个案例为什么不能直接照搬？"
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void submit();
                  }
                }}
              />
              <button type="button" aria-label="发送问题" disabled={!input.trim() || sending} onClick={() => void submit()}>
                <ArrowUp size={19} weight="bold" />
              </button>
            </div>
          </div>
        </>
      )}
    </aside>
  );
}
